// ---------------------------------------------------------------------------
// Email-link helpers. A work item can carry a single `emailLink` pointing
// back to the email that triggered it. Only three shapes are accepted — the
// same allowlist the backend enforces in WorkItemEmailLink.IsAllowed:
//
//   1. mailto:user@example.com?subject=...
//   2. ms-outlook:... (Outlook desktop deep link)
//   3. https://outlook.office.com/ OR outlook.office365.com OR outlook.live.com
//
// Rejecting arbitrary http/https keeps the field from becoming a phishing
// redirector or a tracker storage surface. The helper is pure, tree-shakable,
// and unit-tested alongside the dashboard derivations.
// ---------------------------------------------------------------------------

const ALLOWED_OUTLOOK_HOSTS = new Set([
  'outlook.office.com',
  'outlook.office365.com',
  'outlook.live.com',
]);

export function isAllowedEmailLink(link: string | undefined | null): boolean {
  if (!link) return false;
  const trimmed = link.trim();
  if (trimmed.length === 0 || trimmed.length > 2000) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('mailto:')) return true;
  if (lower.startsWith('ms-outlook:')) return true;
  if (!lower.startsWith('https://')) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' && ALLOWED_OUTLOOK_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

// Scheme allowlist for meeting-brief "linked thread" URLs. Unlike the
// Outlook-only email allowlist above, a thread citation can legitimately point
// at Outlook, Teams or Gmail (web links or app deep links), so we can't pin
// the host — but we MUST pin the scheme. The XSS sink here is the rendered
// `<a href>`: `javascript:`, `data:`, `vbscript:` and empty/whitespace URLs all
// execute or mislead. We allow only `https:` (every provider's web link) plus
// the two safe app deep-link schemes, and reject everything else. A brief is
// pushed in via MCP by Claude Co-Work, so the URL is attacker-influencable —
// this is the gate that keeps a poisoned thread link from running script in
// the app origin. Render non-allowed URLs as inert text, never as a link.
export function isAllowedThreadUrl(link: string | undefined | null): boolean {
  if (!link) return false;
  const trimmed = link.trim();
  if (trimmed.length === 0 || trimmed.length > 2000) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('ms-outlook:')) return true;
  if (lower.startsWith('msteams:')) return true;
  if (!lower.startsWith('https://')) return false;
  try {
    return new URL(trimmed).protocol === 'https:';
  } catch {
    return false;
  }
}

// Human-friendly label for a stored link. "mailto:a@b" becomes the address
// the user pasted; an https Outlook URL becomes "Open in Outlook on the web";
// an ms-outlook: deep link becomes "Open in Outlook".
export function emailLinkKind(link: string): 'mailto' | 'outlook-desktop' | 'outlook-web' | 'unknown' {
  const lower = link.trim().toLowerCase();
  if (lower.startsWith('mailto:')) return 'mailto';
  if (lower.startsWith('ms-outlook:')) return 'outlook-desktop';
  if (lower.startsWith('https://')) {
    try {
      const url = new URL(link);
      if (ALLOWED_OUTLOOK_HOSTS.has(url.hostname.toLowerCase())) return 'outlook-web';
    } catch {
      return 'unknown';
    }
  }
  return 'unknown';
}

// Effective source link for a work item — prefer the explicit `emailLink`
// field when it passes the allowlist, otherwise scan the description and
// notes for the first http(s) URL that DOES pass the allowlist (currently
// outlook.office.com / outlook.office365.com / outlook.live.com). Returns
// null if neither path yields an allowed link.
//
// Why this fallback exists: older work items captured from emails /
// transcripts / MCP often have the source URL embedded in the description
// rather than the `emailLink` field — the backend's transcript-extraction
// + email-ingest paths historically didn't always populate EmailLink at
// create time. Filed in BACKLOG to be fixed server-side; until that lands,
// this client-side scan keeps the Open-in-Outlook icon visible on legacy
// items so the user keeps the one-tap-to-source flow.
//
// Security: the same `isAllowedEmailLink` allowlist still gates every
// resolved URL, so an attacker who plants `https://evil.example/...` in
// a description body can NOT cause the icon to open it — the URL would
// fail the allowlist and be skipped.
export function resolveEmailLink(item: {
  emailLink?: string | null;
  description?: string | null;
  notes?: string | null;
}): string | null {
  if (isAllowedEmailLink(item.emailLink)) {
    return item.emailLink!.trim();
  }
  for (const body of [item.description, item.notes]) {
    if (!body) continue;
    const extracted = extractFirstAllowedHttpUrl(body);
    if (extracted) return extracted;
  }
  return null;
}

// Scan `text` for the first http(s) URL that passes the allowlist. Strips
// trailing sentence punctuation (commas, full stops, parens, etc.) before
// the allowlist check so `... see https://outlook.office365.com/owa/.`
// matches against the URL without the trailing dot.
function extractFirstAllowedHttpUrl(text: string): string | null {
  const matches = text.match(/\bhttps?:\/\/[^\s<>"']+/gi) ?? [];
  for (const raw of matches) {
    const cleaned = raw.replace(/[.,;:!?)\]]+$/, '');
    if (isAllowedEmailLink(cleaned)) return cleaned;
  }
  return null;
}

// Hand the link to the OS / browser. For https Outlook URLs we open a new
// tab with noopener+noreferrer. For mailto: and ms-outlook: URIs we navigate
// the current window — the browser will hand off to the OS default handler
// (Outlook desktop on Mac/Windows if configured, Apple Mail otherwise, etc.).
// In neither case do we ever render the URL as raw HTML, so there is no XSS
// surface here.
export function openEmailLink(link: string | undefined | null): boolean {
  if (!isAllowedEmailLink(link)) return false;
  const kind = emailLinkKind(link!);
  if (kind === 'outlook-web') {
    window.open(link!, '_blank', 'noopener,noreferrer');
    return true;
  }
  if (kind === 'mailto' || kind === 'outlook-desktop') {
    // Assigning location triggers the registered protocol handler. We do not
    // want a new tab here because most browsers refuse to open non-http
    // schemes in window.open and fall back to nothing.
    window.location.assign(link!);
    return true;
  }
  return false;
}
